class SlideshowCarousel extends HTMLElement {
  constructor() {
    super();
    this.currentSlide = 0;
    this.slides = Array.from(this.querySelectorAll(this.getAttribute('cell-selector') || '.slideshow__slide'));
    this.navigationDots = this.querySelectorAll('.page-dots button');
    this.autoplay = this.hasAttribute('autoplay') && this.slides.length > 1;
    this.cycleSpeed = parseInt(this.getAttribute('autoplay') || 5) * 1000;
    this.autoplayPauseOnVideo = this.hasAttribute('autoplay-pause-on-video');
    
    this.initializeSlides();
    this.initializeEventListeners();
    
    if (this.autoplay) {
      this.startAutoplay();
    }
  }

  initializeSlides() {
  this.slides.forEach((slide, index) => {
    const video = slide.querySelector('video');

    if (video) {
      video.setAttribute('playsinline', '');
      video.setAttribute('muted', '');

      video.addEventListener('ended', () => {
        if (this.autoplay && this.autoplayPauseOnVideo) {
          this.startAutoplay();
        }
      });
    }

    const slideLink = slide.getAttribute('href');
    if (slideLink) {
      slide.style.cursor = 'pointer';
      slide.addEventListener('click', (e) => {
        if (e.target.closest('.button-group, .slideshow__volume-control, .page-dots')) {
          e.preventDefault();
          return;
        }
        window.location.href = slideLink;
      });
    }

    const buttons = slide.querySelectorAll('.button-group button, .button-group a');
    buttons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    });
  });

  // **Fix for Single Video Slide Issue**
  if (this.slides.length === 1) {
    const singleVideo = this.slides[0].querySelector('video');
    if (singleVideo) {
      singleVideo.play().catch(error => console.error("Autoplay failed:", error));
    }
  }
}

initializeEventListeners() {
    // Navigation dots
    this.navigationDots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });

    // Next section button
    const nextSectionButton = this.querySelector('[data-action="navigate-next"]');
    if (nextSectionButton) {
      nextSectionButton.addEventListener('click', this.navigateToNextSection.bind(this));
    }

    // Volume control for videos
    const volumeUnmuteButton = this.querySelector('[data-action="unmute"]');
    const volumeMuteButton = this.querySelector('[data-action="mute"]');

    if (volumeUnmuteButton && volumeMuteButton) {
      const videos = this.querySelectorAll('video');
      
      volumeUnmuteButton.addEventListener('click', () => {
        videos.forEach(video => {
          video.muted = false;
          video.play();
        });
        volumeUnmuteButton.hidden = true;
        volumeMuteButton.hidden = false;
      });

      volumeMuteButton.addEventListener('click', () => {
        videos.forEach(video => video.muted = true);
        volumeMuteButton.hidden = true;
        volumeUnmuteButton.hidden = false;
      });
    }

    // Enable swipe and drag functionality
    this.enableSwipeAndDrag();

  // Start autoplay if enabled
    if (this.autoplay) {
        this.startAutoplay();
    }
}

enableSwipeAndDrag() {
    let startX = 0;
    let currentX = 0;
    let isDragging = false;
    let isClickAllowed = true;
    let dragThreshold = 10; // Minimum distance to detect swipe

    const startDrag = (event) => {
        isDragging = true;
        isClickAllowed = true;
        startX = event.type.includes('touch') ? event.touches[0].clientX : event.clientX;
        this.pauseAutoplay(); // Pause autoplay when dragging
    };

    const onDrag = (event) => {
        if (!isDragging) return;

        currentX = event.type.includes('touch') ? event.touches[0].clientX : event.clientX;
        const diff = Math.abs(startX - currentX);

        // If the swipe distance exceeds the threshold, disable clicks
        if (diff > dragThreshold) {
            isClickAllowed = false;
        }
    };

    const endDrag = () => {
        if (!isDragging) return;

        const diff = startX - currentX;

        // Swipe left
        if (diff > 50) {
            this.nextSlide();
        }
        // Swipe right
        else if (diff < -50) {
            this.goToSlide((this.currentSlide - 1 + this.slides.length) % this.slides.length);
        }

        isDragging = false;
        this.startAutoplay(); // Resume autoplay after interaction
    };

    const handleClick = (event) => {
        // Prevent click action if swipe gesture was detected
        if (!isClickAllowed) {
            event.preventDefault();
            event.stopPropagation();
        }
    };

    // **Touch Events (Mobile)**:
    this.addEventListener('touchstart', startDrag, { passive: true });
    this.addEventListener('touchmove', onDrag);
    this.addEventListener('touchend', endDrag);

    // **Mouse Events (Desktop)**:
    this.addEventListener('mousedown', startDrag);
    this.addEventListener('mousemove', onDrag);
    this.addEventListener('mouseup', endDrag);
    this.addEventListener('mouseleave', endDrag);

    // Prevent swipe from blocking link clicks
    this.querySelectorAll('a, button').forEach((element) => {
        element.addEventListener('click', handleClick);
    });
}

  goToSlide(index) {
    // Remove current selection
    this.slides[this.currentSlide].classList.remove('is-selected');
    if (this.navigationDots[this.currentSlide]) {
      this.navigationDots[this.currentSlide].setAttribute('aria-current', 'false');
    }

    // Stop current video if exists
    const currentVideo = this.slides[this.currentSlide].querySelector('video');
    if (currentVideo) {
      currentVideo.pause();
    }

    // Set new slide
    this.currentSlide = index;
    this.slides[this.currentSlide].classList.add('is-selected');
    if (this.navigationDots[this.currentSlide]) {
      this.navigationDots[this.currentSlide].setAttribute('aria-current', 'true');
    }

    // Handle video in new slide
    const newVideo = this.slides[this.currentSlide].querySelector('video');
    if (newVideo) {
      newVideo.currentTime = 0;
      newVideo.play().catch(() => {});
      
      if (this.autoplay && this.autoplayPauseOnVideo) {
        this.pauseAutoplay();
      }
    } else if (this.autoplay) {
      this.resetAutoplayTimer();
    }
  }

  nextSlide() {
    const nextIndex = (this.currentSlide + 1) % this.slides.length;
    this.goToSlide(nextIndex);
  }

  startAutoplay() {
    if (!this.autoplayTimer && this.slides.length > 1) {
      this.autoplayTimer = setInterval(() => this.nextSlide(), this.cycleSpeed);
    }
  }

  pauseAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  resetAutoplayTimer() {
    this.pauseAutoplay();
    this.startAutoplay();
  }

  navigateToNextSection() {
    const nextSection = this.nextElementSibling;
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: 'smooth' });
    }
  }

  disconnectedCallback() {
    this.pauseAutoplay();
  }
}

customElements.define('slideshow-carousel', SlideshowCarousel);

// Carousel Navigation Element
class CarouselNavigation extends HTMLElement {
  constructor() {
    super();
    this.carouselId = this.getAttribute('aria-controls');
    this.carousel = document.getElementById(this.carouselId);
  }
}

customElements.define('carousel-navigation', CarouselNavigation);